import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import ProBadge from "@/components/ProBadge";
import DemoBanner from "@/components/DemoBanner";
import LikeButton from "@/components/LikeButton";
import ShareButton from "@/components/ShareButton";
import FeaturedPartners from "@/components/FeaturedPartners";
import PressOffers from "@/components/PressOffers";
import EventBrandMentions from "@/components/EventBrandMentions";
import { ArrowLeft, MapPin, MessageSquare, Flag, Trash2, Send, Phone, Search, HelpingHand, ShieldAlert, CheckCircle2, Heart, Award, ImagePlus, Loader2, X } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

const KIND_META = {
  request: { label: "Je cherche", cls: "bg-orange-100 text-orange-700", icon: Search },
  offer: { label: "Je propose", cls: "bg-emerald-100 text-emerald-700", icon: HelpingHand },
};

export default function EntreVoisinsDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [text, setText] = useState("");
  const [contact, setContact] = useState("");
  const [rName, setRName] = useState("");
  const [rEmail, setREmail] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [thankOpen, setThankOpen] = useState(null);
  const [thankNote, setThankNote] = useState("");

  const load = useCallback(() => {
    api.get(`/entre-voisins/${id}`).then((r) => { setPost(r.data.post); setReplies(r.data.replies || []); })
      .catch(() => setNotFound(true));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = 3 - photos.length;
    if (room <= 0) { toast.error("3 photos maximum."); return; }
    if (files.length > room) toast.error("3 photos maximum.");
    setUploading(true);
    try {
      for (const file of files.slice(0, room)) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/entre-voisins/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((ps) => (ps.length < 3 ? [...ps, data.url] : ps));
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const reply = async () => {
    if (text.trim().length < 2) { toast.error("Votre réponse est trop courte."); return; }
    const anon = !(user && user !== false);
    if (anon && (!rEmail.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(rEmail.trim()))) { toast.error("Ajoutez votre e-mail pour recevoir les réponses."); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/entre-voisins/${id}/replies`, { text, contact, photos, name: rName, email: rEmail });
      setText(""); setContact(""); setRName(""); setREmail(""); setPhotos([]); load();
      toast.success(data.account_created ? "Réponse publiée ! Un compte membre a été créé — vos identifiants vous ont été envoyés par e-mail." : "Réponse publiée");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const reportPost = async () => {
    try { await api.post(`/entre-voisins/${id}/report`); toast.success("Annonce signalée et masquée en attendant la modération."); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const reportReply = async (rid) => {
    try { await api.post(`/entre-voisins/replies/${rid}/report`); setReplies((xs) => xs.filter((x) => x.id !== rid)); toast.success("Réponse signalée."); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const delReply = async (rid) => {
    try { await api.delete(`/entre-voisins/replies/${rid}`); setReplies((xs) => xs.filter((x) => x.id !== rid)); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleResolve = async () => {
    try { const { data } = await api.post(`/entre-voisins/${id}/resolve`); setPost((p) => ({ ...p, resolved: data.resolved })); toast.success(data.resolved ? "Annonce marquée comme résolue ✅" : "Annonce rouverte"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const submitThanks = async (rid) => {
    try { await api.post(`/entre-voisins/${id}/thanks`, { reply_id: rid, note: thankNote }); setThankOpen(null); setThankNote(""); load(); toast.success("Merci envoyé à votre voisin ❤️"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <p className="text-slate-500">Cette annonce n'existe plus ou a été masquée.</p>
      <Link to="/entre-voisins" className="text-[#E05638] font-semibold mt-3 inline-block">← Retour à Entre Voisins</Link>
    </div>
  );
  if (!post) return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-slate-400">Chargement…</div>;

  const photoPicker = (
    <div className="mt-2" data-testid="voisin-reply-photos">
      <div className="flex flex-wrap gap-2">
        {photos.map((u, i) => (
          <div key={u} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200">
            <img src={u} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => setPhotos((ps) => ps.filter((x) => x !== u))} className="absolute top-0 right-0 bg-black/60 text-white p-0.5 rounded-bl" data-testid={`voisin-reply-photo-remove-${i}`}><X size={11} /></button>
          </div>
        ))}
        {photos.length < 3 && (
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-[#E05638] hover:text-[#E05638] grid place-items-center transition-colors" data-testid="voisin-reply-photo-add">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={18} />}
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onPickPhotos} data-testid="voisin-reply-photo-input" />
      </div>
      <p className="mt-1 text-[11px] text-slate-400">Ajoutez jusqu'à 3 photos (facultatif).</p>
    </div>
  );

  const km = KIND_META[post.kind] || KIND_META.request;
  const KindIcon = km.icon;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="voisin-detail-page">
      <Link to="/entre-voisins" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#E05638] bg-orange-50 hover:bg-orange-100 border border-orange-200 px-3.5 py-2 rounded-full transition-colors" data-testid="voisin-back"><ArrowLeft size={15} /> Retour à Entre Voisins</Link>
      {post.demo && <DemoBanner className="mt-5" />}

      <div className="mt-5 bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${km.cls}`}><KindIcon size={12} /> {km.label}</span>
          <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{post.category}</span>
          {post.is_pro && <ProBadge size="sm" testId="voisin-detail-pro" />}
          {post.resolved && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-700" data-testid="voisin-resolved-badge"><CheckCircle2 size={12} /> Résolu</span>}
        </div>
        <h1 className="mt-3 font-heading text-2xl font-extrabold text-slate-900">{post.title}</h1>
        <div className="mt-2 flex items-center gap-3 text-sm text-slate-500 flex-wrap">
          <Link to={`/voisin/${post.author_id}`} className="font-semibold text-slate-700 hover:text-[#E05638]" data-testid="voisin-detail-author">{post.author_name}</Link>
          {post.city && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {post.city}{post.postal_code ? ` (${post.postal_code})` : ""}</span>}
          <span>{timeAgo(post.created_at)}</span>
        </div>
        {(post.photos?.length ? post.photos : (post.photo ? [post.photo] : [])).length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-2" data-testid="voisin-detail-gallery">
            {(post.photos?.length ? post.photos : [post.photo]).map((u, i) => (
              <img key={i} src={u} alt={post.title} className={`rounded-xl w-full object-cover ${i === 0 ? "col-span-2 max-h-80" : "h-40"}`} />
            ))}
          </div>
        )}
        <p className="mt-4 text-slate-700 whitespace-pre-line">{post.description}</p>
        {post.contact && (
          <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-slate-800 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="voisin-detail-contact">
            <Phone size={14} className="text-[#E05638]" /> {post.contact}
          </div>
        )}
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <LikeButton type="voisin" id={post.id} liked={post.liked} count={post.likes} testId="voisin-like-btn" />
          <ShareButton url={`${process.env.REACT_APP_BACKEND_URL}/api/voisin-vitrine/${post.id}`} title={post.title} text={`${post.title} — Entre Voisins · ${post.city || "Eure-et-Loir"}`} testId="voisin-share-btn" className="h-9" />
          <button onClick={reportPost} className="text-xs text-slate-400 hover:text-amber-600 inline-flex items-center gap-1" data-testid="voisin-report-post"><Flag size={12} /> Signaler cette annonce</button>
          {post.is_author && (
            <button onClick={toggleResolve} className={`text-xs font-semibold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-colors ${post.resolved ? "border-slate-200 text-slate-600 hover:bg-slate-50" : "border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"}`} data-testid="voisin-resolve-btn">
              <CheckCircle2 size={13} /> {post.resolved ? "Rouvrir l'annonce" : "Marquer comme résolu"}
            </button>
          )}
        </div>
      </div>

      {/* Réponses */}
      <h2 className="mt-8 font-heading text-lg font-bold text-slate-900 flex items-center gap-2"><MessageSquare size={18} className="text-[#E05638]" /> Propositions & réponses {replies.length > 0 && <span className="text-slate-400 font-normal">({replies.length})</span>}</h2>

      {user && user !== false ? (
        <div className="mt-4 bg-white rounded-2xl border border-slate-200 p-4">
          <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Proposez votre aide ou posez une question…" data-testid="voisin-reply-input" />
          <Input className="mt-2" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Contact à afficher (optionnel)" data-testid="voisin-reply-contact" />
          {photoPicker}
          <div className="mt-2 flex justify-end">
            <Button onClick={reply} disabled={sending} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="voisin-reply-submit"><Send size={15} className="mr-1.5" /> Répondre</Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 bg-white rounded-2xl border border-slate-200 p-4" data-testid="voisin-reply-box-anon">
          <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Proposez votre aide ou posez une question…" data-testid="voisin-reply-input" />
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input value={rName} onChange={(e) => setRName(e.target.value)} placeholder="Votre prénom (facultatif)" data-testid="voisin-reply-anon-name" />
            <Input type="email" value={rEmail} onChange={(e) => setREmail(e.target.value)} placeholder="Votre e-mail" data-testid="voisin-reply-anon-email" />
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Votre e-mail sert à recevoir les réponses — un espace membre gratuit est créé, accès envoyé par e-mail.</p>
          <Input className="mt-2" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Contact à afficher (optionnel)" data-testid="voisin-reply-contact" />
          {photoPicker}
          <div className="mt-2 flex justify-end">
            <Button onClick={reply} disabled={sending} className="bg-[#E05638] hover:bg-[#c94a2f]" data-testid="voisin-reply-submit"><Send size={15} className="mr-1.5" /> Répondre</Button>
          </div>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {replies.length === 0 && <div className="text-sm text-slate-400 text-center py-6">Aucune réponse pour l'instant.</div>}
        {replies.map((r) => (
          <div key={r.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`voisin-reply-${r.id}`}>
            <div className="flex items-center gap-2 flex-wrap text-xs text-slate-500">
              <Link to={`/voisin/${r.author_id}`} className="font-semibold text-slate-800 hover:text-[#E05638]" data-testid={`voisin-reply-author-${r.id}`}>{r.author_name}</Link>
              {r.is_pro && <ProBadge size="sm" testId={`voisin-reply-pro-${r.id}`} />}
              {r.helper_thanks > 0 && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-600 font-bold" title="Coups de main remerciés"><Award size={11} /> {r.helper_thanks}</span>}
              <span>{timeAgo(r.created_at)}</span>
            </div>
            <p className="mt-1.5 text-sm text-slate-700 whitespace-pre-line">{r.text}</p>
            {r.photos?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid={`voisin-reply-gallery-${r.id}`}>
                {r.photos.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noreferrer" className="block">
                    <img src={u} alt="" className="w-24 h-24 rounded-lg object-cover border border-slate-200 hover:opacity-90 transition-opacity" />
                  </a>
                ))}
              </div>
            )}
            {r.contact && <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1"><Phone size={12} className="text-[#E05638]" /> {r.contact}</div>}
            {r.thanked && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5" data-testid={`voisin-reply-thanked-${r.id}`}>
                <Heart size={12} className="fill-rose-500 text-rose-500" /> Remercié par l'auteur{r.thanks_note ? ` : « ${r.thanks_note} »` : ""}
              </div>
            )}
            <div className="mt-2 flex items-center gap-4 text-xs">
              <button onClick={() => reportReply(r.id)} className="text-slate-400 hover:text-amber-600 inline-flex items-center gap-1" data-testid={`voisin-reply-report-${r.id}`}><Flag size={12} /> Signaler</button>
              {r.mine && <button onClick={() => delReply(r.id)} className="text-slate-400 hover:text-rose-600 inline-flex items-center gap-1" data-testid={`voisin-reply-delete-${r.id}`}><Trash2 size={12} /> Supprimer</button>}
              {post.is_author && !r.mine && !r.thanked && (
                <button onClick={() => { setThankOpen(thankOpen === r.id ? null : r.id); setThankNote(""); }} className="text-[#E05638] font-semibold hover:text-[#c94a2f] inline-flex items-center gap-1" data-testid={`voisin-reply-thank-${r.id}`}><Heart size={12} /> Remercier</button>
              )}
            </div>
            {thankOpen === r.id && (
              <div className="mt-2 flex gap-2">
                <Input value={thankNote} onChange={(e) => setThankNote(e.target.value)} placeholder="Un petit mot de remerciement (optionnel)…" data-testid={`voisin-thank-input-${r.id}`} />
                <Button size="sm" onClick={() => submitThanks(r.id)} className="bg-[#E05638] hover:bg-[#c94a2f] shrink-0" data-testid={`voisin-thank-submit-${r.id}`}><Heart size={14} /></Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-3">
        <ShieldAlert size={14} className="text-slate-400 mt-0.5 shrink-0" />
        <span>Entraide gratuite entre voisins. PROXIreduc-28 n'intervient pas dans les échanges et décline toute responsabilité ; soyez prudent et signalez tout abus.</span>
      </div>

      {/* Blocs partenaires / offres / marque — tout en bas de la page */}
      <FeaturedPartners limit={5} testId="voisin-featured-partners" city={post.city || ""} />
      <PressOffers limit={4} testId="voisin-press-offers" city={post.city || ""} />
      <div className="mt-12 pt-8 border-t border-slate-100">
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Partenaires</div>
        <EventBrandMentions testId="voisin-brand-mentions" />
      </div>
    </div>
  );
}
